import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import Helmet from 'react-helmet';

import {
    ServerVarsContextConsumer,
    ServerVarsContextType,
} from '../../global/ServerVarsContext/ServerVarsContext';
import { addTrailingSlash } from '../../utils/uriUtils';

import { SharedHeadLinkTags_metadata } from './__generated__/SharedHeadLinkTags_metadata.graphql';

type Props = {
    metadata: SharedHeadLinkTags_metadata;
    localeOrigin?: ServerVarsContextType['localeOrigin'];
    additionalLinks?: { rel: string; href: string }[];
};

const SharedHeadLinkTagsComponent: FC<Props> = ({
    metadata,
    localeOrigin,
    additionalLinks = [],
}) => {
    const linkTagsArray = [...additionalLinks];
    if (!metadata.disableIndexing) {
        const { canonical, prev, next } = metadata;

        linkTagsArray.push({
            rel: 'canonical',
            href: addTrailingSlash(`${localeOrigin}/${canonical}`),
        });

        if (prev) {
            linkTagsArray.push({
                rel: 'prev',
                href: addTrailingSlash(`${localeOrigin}/${prev}`),
            });
        }

        if (next) {
            linkTagsArray.push({
                rel: 'next',
                href: addTrailingSlash(`${localeOrigin}/${next}`),
            });
        }
    }

    return <Helmet link={linkTagsArray} />;
};

const SharedHeadLinkTagsServerVarsContextComponent: FC<Props> = props => {
    return (
        <ServerVarsContextConsumer>
            {({ localeOrigin }) => (
                <SharedHeadLinkTagsComponent {...props} localeOrigin={localeOrigin} />
            )}
        </ServerVarsContextConsumer>
    );
};

export const SharedHeadLinkTags = createFragmentContainer(
    SharedHeadLinkTagsServerVarsContextComponent,
    {
        metadata: graphql`
            fragment SharedHeadLinkTags_metadata on MetaData {
                canonical
                disableIndexing
                next
                prev
            }
        `,
    }
);
